import { MaraDialog } from "@kalin-setterberg/mara-elements";
import { emmet } from "emmet-elem";

export interface ShellPage {
    id: string;
    label: string;
    /** Inline SVG markup for the sidebar icon. */
    icon: string;
    element: HTMLElement;
}

const svg = (body: string, size = 22) =>
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

export const ICONS = {
    home: svg(
        '<path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-4.5v-6h-5v6H5a1 1 0 0 1-1-1z"/>',
        18
    ),
    chevron: svg('<path d="m9 6 6 6-6 6"/>', 16),
    help: svg(
        '<circle cx="12" cy="12" r="9"/><path d="M9.6 9.3a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.2-2.4 3.6"/><path d="M12 17h.01"/>'
    ),
    servers: svg(
        '<rect x="4" y="4" width="16" height="7" rx="1.5"/><rect x="4" y="13" width="16" height="7" rx="1.5"/><path d="M8 7.5h.01M8 16.5h.01"/>'
    ),
    instances: svg(
        '<ellipse cx="12" cy="6" rx="7" ry="2.5"/><path d="M5 6v12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5V6"/><path d="M5 12c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5"/>'
    ),
    monitor: svg('<path d="M3 12h4l3-7 4 14 3-7h4"/>'),
    user: svg(
        '<circle cx="12" cy="9" r="3.5"/><path d="M5.5 19a6.5 6.5 0 0 1 13 0"/>',
        16
    ),
};

const LOGO = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="12" viewBox="0 0 39 15" fill="none" aria-hidden="true">
<path d="M38.571 1.87495C38.571 0.839433 37.6135 0 36.4325 0H33.9599H21.1289H19.2746C17.7498 0 16.3136 0.629575 15.3987 1.69953L8.68289 9.55057C7.76798 10.6205 6.33184 11.2501 4.80653 11.2501H2.13849C0.957424 11.2501 0 12.0895 0 13.1251C0 14.1606 0.957424 15 2.13849 15H4.02146H16.8524H18.7067C20.232 15 21.6682 14.3704 22.5831 13.3005L29.2989 5.44943C30.2138 4.37947 31.65 3.7499 33.1753 3.7499H36.4329C37.614 3.7499 38.5714 2.91047 38.5714 1.87495H38.571Z" fill="url(#shell-logo)"/>
<defs><linearGradient id="shell-logo" x1="1.40194" y1="12.7827" x2="34.755" y2="-0.249859" gradientUnits="userSpaceOnUse">
<stop stop-color="#35C2FF"/><stop offset="1" stop-color="#FF2E82"/>
</linearGradient></defs>
</svg>`;

const STYLE = `
body {
    display: grid;
    grid-template-columns: 50px minmax(0, 1fr);
    grid-template-rows: 46px minmax(0, 1fr);
    grid-template-areas: "top top" "side main";
    font-family: var(--font-sans);
    font-size: 15px;

    --shell-navy: #2a4259;
    --shell-navy-active: #3e5c78;
    --shell-icon: #e6edf3;
    --shell-crumb: #f3b31b;
    --split-color: #0b4f7c;
    --split-hover: #0d6299;
    --shell-gradient: linear-gradient(90deg, #233f5c 0%, #0b4f8c 32%, #3a3375 58%, #7a0e4f 80%, #3f2c6c 100%);

    --strong-text: #222;
    --table-text: #333;
    --table-header: #f3f3f3;
    --grid-line: #e3e3e3;
    --pill-border: #cfd3d8;
    --pill-border-hover: #a9b0b8;
    --placeholder-text: #9a9a9a;
    --row-selected: #e4eff8;
    --item-hover: #f5f7f9;
    --item-active: #e4eff8;
    --item-focus: #dbe9f5;
}
#shell-top {
    grid-area: top;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-right: 16px;
    background: var(--shell-gradient);
    color: #fff;
    min-width: 0;
}
#shell-top .logo {
    flex: 0 0 50px;
    display: grid;
    place-items: center;
    height: 100%;
    border-right: 1px solid rgba(255, 255, 255, 0.12);
}
.crumbs {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: 8px;
    font-size: 16px;
    min-width: 0;
}
.crumbs > svg {
    opacity: 0.85;
}
.crumbs .page {
    color: var(--shell-crumb);
    white-space: nowrap;
}
.shell-icon-btn {
    display: inline-grid;
    place-items: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;
}
.shell-icon-btn:hover,
.shell-icon-btn:focus-visible {
    background: rgba(255, 255, 255, 0.14);
    outline: none;
}
#shell-side {
    grid-area: side;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background: var(--shell-navy);
    color: var(--shell-icon);
    padding: 4px 0 12px;
}
#shell-side .nav {
    display: grid;
    place-items: center;
    height: 44px;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;
}
#shell-side .nav:hover,
#shell-side .nav:focus-visible {
    background: rgba(255, 255, 255, 0.08);
    outline: none;
}
#shell-side .nav[aria-current="page"] {
    background: var(--shell-navy-active);
}
#shell-side .spacer {
    flex: 1 1 auto;
}
#shell-side .shell-icon-btn {
    justify-self: center;
    align-self: center;
}
#shell-side .avatar {
    align-self: center;
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    margin-top: 12px;
    border-radius: 50%;
    border: 1.5px solid #fff;
    background: #1f5fae;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    cursor: default;
}
#shell-main {
    grid-area: main;
    display: grid;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    background: var(--surface);
}
#shell-main > * {
    min-width: 0;
    min-height: 0;
}
`;

function html(markup: string): DocumentFragment {
    const t = document.createElement("template");
    t.innerHTML = markup;
    return t.content;
}

/** Initials and display name from the session token, if it is a JWT. */
export function userFromToken(token: string): {
    initials: string;
    name: string;
} {
    try {
        const part = token.split(".")[1];
        if (part) {
            const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
            const claims = JSON.parse(json) as Record<string, unknown>;
            const name = [
                claims.name,
                claims.preferred_username,
                claims.email,
            ].find((x) => typeof x === "string" && x) as string | undefined;
            if (name) {
                const words = name
                    .split("@")[0]
                    .split(/[\s._-]+/)
                    .filter(Boolean);
                const initials =
                    words.length > 1
                        ? words[0][0] + words[words.length - 1][0]
                        : (words[0] || "").slice(0, 2);
                return { initials: initials.toUpperCase(), name };
            }
        }
    } catch (e) {
        // Opaque token: fall back to a generic avatar.
    }
    return { initials: "", name: "" };
}

/**
 * Builds the page chrome (gradient top bar, navy side bar) around the pages
 * and returns a function that switches the visible page.
 */
export function mountShell(
    pages: ShellPage[],
    user: { initials: string; name: string }
): (id: string) => void {
    const style = document.createElement("style");
    style.textContent = STYLE;
    document.head.appendChild(style);

    while (document.body.lastChild) {
        document.body.removeChild(document.body.lastChild);
    }

    const top = emmet`header#shell-top` as HTMLElement;
    top.appendChild(html(`<div class="logo">${LOGO}</div>`));
    const crumbs = emmet`nav.crumbs[aria-label=Breadcrumb]` as HTMLElement;
    const home =
        emmet`button.shell-icon-btn[title=Home aria-label=Home]` as HTMLButtonElement;
    home.appendChild(html(ICONS.home));
    crumbs.appendChild(home);
    crumbs.appendChild(html(ICONS.chevron));
    const pageName = emmet`span.page[aria-current=page]` as HTMLSpanElement;
    crumbs.appendChild(pageName);
    top.appendChild(crumbs);

    const side = emmet`nav#shell-side[aria-label=Pages]` as HTMLElement;
    const navs = new Map<string, HTMLButtonElement>();
    for (const page of pages) {
        const btn = emmet`button.nav` as HTMLButtonElement;
        btn.title = page.label;
        btn.setAttribute("aria-label", page.label);
        btn.appendChild(html(page.icon));
        btn.addEventListener("click", () => show(page.id));
        navs.set(page.id, btn);
        side.appendChild(btn);
    }
    side.appendChild(emmet`div.spacer`);
    const help =
        emmet`button.shell-icon-btn[title=Help aria-label=Help]` as HTMLButtonElement;
    help.appendChild(html(ICONS.help));
    help.addEventListener("click", showHelp);
    side.appendChild(help);
    const avatar = emmet`div.avatar` as HTMLDivElement;
    if (user.initials) {
        avatar.textContent = user.initials;
    } else {
        avatar.appendChild(html(ICONS.user));
    }
    avatar.title = user.name || "Signed in";
    side.appendChild(avatar);

    const main = emmet`main#shell-main` as HTMLElement;
    for (const page of pages) {
        main.appendChild(page.element);
    }

    document.body.appendChild(top);
    document.body.appendChild(side);
    document.body.appendChild(main);

    function show(id: string) {
        const page = pages.find((p) => p.id === id) || pages[0];
        for (const p of pages) {
            p.element.style.display = p === page ? "" : "none";
            const btn = navs.get(p.id);
            if (p === page) {
                btn?.setAttribute("aria-current", "page");
            } else {
                btn?.removeAttribute("aria-current");
            }
        }
        pageName.textContent = page.label;
        if (location.hash !== "#" + page.id) {
            history.replaceState(null, "", "#" + page.id);
        }
    }

    home.addEventListener("click", () => show(pages[0].id));

    window.addEventListener("hashchange", () => show(location.hash.slice(1)));
    show(location.hash.slice(1));
    return show;
}

let helpDialog: MaraDialog<HTMLElement> | undefined;

function showHelp() {
    // Dialog content renders inside the dialog's shadow root, so style inline.
    const dt = 'style="font-weight:700;margin-top:10px"';
    const dd = 'style="margin:0"';
    const div = document.createElement("div");
    div.style.cssText = "max-width:34em;padding:0 16px 12px;line-height:1.5";
    div.appendChild(
        html(`<dl style="margin:0">
<dt ${dt}>Servers</dt><dd ${dd}>Admin daemons and their proxies. Refresh reloads the table.</dd>
<dt ${dt}>Instances</dt><dd ${dd}>Select rows, then use the toolbar actions. Install, Service Session, Services, Host Settings, Test Email, Logs and DTA work on exactly one selected row.</dd>
<dt ${dt}>Monitor</dt><dd ${dd}>Latency, error rate and request rate of a service, from the traces in Elasticsearch. Pick service, domain, range and bucket size; sort the operations table by clicking a column, and click an operation to list its failed requests.</dd>
<dt ${dt}>Search</dt><dd ${dd}>Filters rows by their values.</dd>
<dt ${dt}>Configure columns</dt><dd ${dd}>Shows or hides table columns.</dd>
</dl>`)
    );
    helpDialog ??= new MaraDialog();
    helpDialog.show(div, {
        heading: "Help",
        closeButton: true,
        type: "center",
        actions: [],
    });
}
